import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createFamily, CreateFamilyPayload, GradeLevel } from '../api/families';
import { RELATIONSHIP_OPTIONS } from '../constants/relationships';

const GRADE_OPTIONS: { value: GradeLevel; label: string }[] = [
  { value: 'K', label: 'Kindergarten' },
  { value: '1', label: '1st Grade' },
  { value: '2', label: '2nd Grade' },
  { value: '3', label: '3rd Grade' },
  { value: '4', label: '4th Grade' },
  { value: '5', label: '5th Grade' },
  { value: '6', label: '6th Grade' },
  { value: '7', label: '7th Grade' },
  { value: '8', label: '8th Grade' },
];

interface StudentFormData {
  first_name: string;
  last_name: string;
  grade: GradeLevel | '';
  medical_notes: string;
  pickup_type: 'car' | 'foot';
}

interface AuthorizedPersonFormData {
  first_name: string;
  last_name: string;
  relationship: string;
  phone: string;
  email: string;
  has_pickup_rights: boolean;
  id_check_required: boolean;
}

const FamilyRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Primary contact relationship (required field)
  const [primaryContactRelationship, setPrimaryContactRelationship] = useState<string>('');

  const [students, setStudents] = useState<StudentFormData[]>([
    { first_name: '', last_name: '', grade: '', medical_notes: '', pickup_type: 'car' },
  ]);

  const [authorizedPersons, setAuthorizedPersons] = useState<AuthorizedPersonFormData[]>([
    {
      first_name: '',
      last_name: '',
      relationship: '',
      phone: '',
      email: '',
      has_pickup_rights: true,
      id_check_required: false,
    },
  ]);

  const steps = ['Your Relationship', 'Add Students', 'Add Authorized Persons', 'Review & Submit'];

  const addStudent = () => {
    setStudents([
      ...students,
      { first_name: '', last_name: '', grade: '', medical_notes: '', pickup_type: 'car' },
    ]);
  };

  const removeStudent = (index: number) => {
    if (students.length > 1) {
      setStudents(students.filter((_, i) => i !== index));
    }
  };

  const updateStudent = (index: number, field: keyof StudentFormData, value: any) => {
    const updated = [...students];
    updated[index] = { ...updated[index], [field]: value };
    setStudents(updated);
  };

  const addAuthorizedPerson = () => {
    setAuthorizedPersons([
      ...authorizedPersons,
      {
        first_name: '',
        last_name: '',
        relationship: '',
        phone: '',
        email: '',
        has_pickup_rights: true,
        id_check_required: false,
      },
    ]);
  };

  const removeAuthorizedPerson = (index: number) => {
    if (authorizedPersons.length > 1) {
      setAuthorizedPersons(authorizedPersons.filter((_, i) => i !== index));
    }
  };

  const updateAuthorizedPerson = (
    index: number,
    field: keyof AuthorizedPersonFormData,
    value: any
  ) => {
    const updated = [...authorizedPersons];
    updated[index] = { ...updated[index], [field]: value };
    setAuthorizedPersons(updated);
  };

  const validateStep = (step: number): boolean => {
    if (step === 0) {
      // Validate primary contact relationship
      return primaryContactRelationship.trim() !== '';
    } else if (step === 1) {
      // Validate students
      return students.every(
        (s) => s.first_name.trim() && s.last_name.trim() && s.grade
      );
    } else if (step === 2) {
      // Validate authorized persons (optional, but if provided must be valid)
      if (authorizedPersons.length > 0 && authorizedPersons[0].first_name.trim()) {
        return authorizedPersons.every(
          (p) => p.first_name.trim() && p.last_name.trim() && p.relationship.trim() && p.phone.trim()
        );
      }
      return true; // Optional persons
    }
    return true;
  };

  const handleNext = () => {
    setError(null);
    if (!validateStep(activeStep)) {
      setError('Please fill in all required fields');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Filter out empty authorized persons
      const validAuthorizedPersons = authorizedPersons.filter(p => p.first_name.trim());

      const payload: CreateFamilyPayload = {
        primary_contact_relationship: primaryContactRelationship,
        students: students.map((s) => ({
          first_name: s.first_name,
          last_name: s.last_name,
          grade: s.grade as GradeLevel,
          medical_notes: s.medical_notes || undefined,
          pickup_type: s.pickup_type,
        })),
        authorized_persons: validAuthorizedPersons.length > 0 ? validAuthorizedPersons.map((p) => ({
          first_name: p.first_name,
          last_name: p.last_name,
          relationship: p.relationship,
          phone: p.phone,
          email: p.email || undefined,
          has_pickup_rights: p.has_pickup_rights,
        })) : undefined,
      };

      await createFamily(payload);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Family Registration
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Register your family to enable quick check-ins and manage authorized pickup persons.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Primary Contact Relationship */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Your Relationship to Students
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please select your relationship to the students you're registering.
              You will be automatically added as the primary contact with pickup authorization.
            </Typography>

            <TextField
              select
              fullWidth
              label="Your Relationship"
              value={primaryContactRelationship}
              onChange={(e) => setPrimaryContactRelationship(e.target.value)}
              required
              sx={{ mt: 2 }}
              helperText="This describes your relationship to the students in this family"
            >
              {RELATIONSHIP_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Step 1: Students */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add Your Students
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add at least one student. You can add more students later.
            </Typography>

            {students.map((student, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">Student {index + 1}</Typography>
                  {students.length > 1 && (
                    <IconButton size="small" onClick={() => removeStudent(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="First Name"
                    required
                    value={student.first_name}
                    onChange={(e) => updateStudent(index, 'first_name', e.target.value)}
                  />
                  <TextField
                    label="Last Name"
                    required
                    value={student.last_name}
                    onChange={(e) => updateStudent(index, 'last_name', e.target.value)}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Grade"
                    select
                    required
                    value={student.grade}
                    onChange={(e) => updateStudent(index, 'grade', e.target.value)}
                  >
                    {GRADE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Default Pickup Type"
                    select
                    value={student.pickup_type}
                    onChange={(e) => updateStudent(index, 'pickup_type', e.target.value)}
                  >
                    <MenuItem value="car">Car</MenuItem>
                    <MenuItem value="foot">Foot</MenuItem>
                  </TextField>
                </Box>

                <TextField
                  label="Medical Notes (Optional)"
                  fullWidth
                  multiline
                  rows={2}
                  value={student.medical_notes}
                  onChange={(e) => updateStudent(index, 'medical_notes', e.target.value)}
                  placeholder="Allergies, medications, special needs, etc."
                />
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={addStudent} sx={{ mt: 1 }}>
              Add Another Student
            </Button>
          </Box>
        )}

        {/* Step 2: Authorized Persons */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Add Authorized Persons
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add people authorized to pick up your students. At least one person must have pickup
              rights.
            </Typography>

            {authorizedPersons.map((person, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1">Authorized Person {index + 1}</Typography>
                  {authorizedPersons.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeAuthorizedPerson(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="First Name"
                    required
                    value={person.first_name}
                    onChange={(e) => updateAuthorizedPerson(index, 'first_name', e.target.value)}
                  />
                  <TextField
                    label="Last Name"
                    required
                    value={person.last_name}
                    onChange={(e) => updateAuthorizedPerson(index, 'last_name', e.target.value)}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Relationship"
                    required
                    select
                    value={person.relationship}
                    onChange={(e) => updateAuthorizedPerson(index, 'relationship', e.target.value)}
                  >
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Phone"
                    required
                    value={person.phone}
                    onChange={(e) => updateAuthorizedPerson(index, 'phone', e.target.value)}
                    placeholder="555-1234"
                  />
                </Box>

                <TextField
                  label="Email (Optional)"
                  fullWidth
                  type="email"
                  value={person.email}
                  onChange={(e) => updateAuthorizedPerson(index, 'email', e.target.value)}
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={person.has_pickup_rights}
                      onChange={(e) =>
                        updateAuthorizedPerson(index, 'has_pickup_rights', e.target.checked)
                      }
                    />
                  }
                  label="Authorized to pick up students"
                />
                {person.has_pickup_rights && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={person.id_check_required}
                        onChange={(e) =>
                          updateAuthorizedPerson(index, 'id_check_required', e.target.checked)
                        }
                      />
                    }
                    label="Require ID verification at pickup"
                    sx={{ ml: 4 }}
                  />
                )}
              </Paper>
            ))}

            <Button startIcon={<AddIcon />} onClick={addAuthorizedPerson} sx={{ mt: 1 }}>
              Add Another Person
            </Button>
          </Box>
        )}

        {/* Step 3: Review */}
        {activeStep === 3 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Information
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Students ({students.length})
            </Typography>
            {students.map((student, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 1 }}>
                <Typography>
                  <strong>
                    {student.first_name} {student.last_name}
                  </strong>{' '}
                  - Grade {GRADE_OPTIONS.find((g) => g.value === student.grade)?.label}
                </Typography>
                {student.medical_notes && (
                  <Typography variant="body2" color="text.secondary">
                    Medical: {student.medical_notes}
                  </Typography>
                )}
              </Paper>
            ))}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Authorized Persons ({authorizedPersons.length})
            </Typography>
            {authorizedPersons.map((person, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 1 }}>
                <Typography>
                  <strong>
                    {person.first_name} {person.last_name}
                  </strong>{' '}
                  ({person.relationship})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {person.phone} {person.email && ` • ${person.email}`}
                  {person.has_pickup_rights && ' • ✓ Can pick up students'}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button onClick={() => navigate('/dashboard')} disabled={loading}>
            Cancel
          </Button>
          <Box>
            {activeStep > 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }} disabled={loading}>
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Registering...' : 'Complete Registration'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default FamilyRegistrationPage;
